import { AppShell, Burger, Group, Menu, NavLink, ScrollArea, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { ReactNode } from 'react';
import { NavLink as RouterNavLink, Link, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/useAuth.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { CompanySwitcher } from './CompanySwitcher.js';
import { SignOutButton } from './SignOutButton.js';

/**
 * The frame every signed-in page renders inside.
 *
 * ## The organising principle: group by scope, and never mix the two
 *
 * A page either changes when you switch company or it does not, and the
 * navigation has to say which without being read carefully.
 *
 * - **Company-scoped** pages are in the sidebar. The ones that are about
 *   configuring the company rather than working in it sit under an
 *   "Indstillinger" group, whose first entry is named after the active company
 *   and renames itself on every switch.
 * - **The installed mobile app** (`/app`) is not in this frame at all. See
 *   MobileAppPage.
 * - **User-scoped** pages — Min konto and MCP — are in the header, beside the
 *   company switcher and sign-out. That corner is already the "who am I"
 *   cluster.
 *
 * Getting this wrong is the same class of error as the switcher showing UUID
 * prefixes: a screen implying a scope that is not real. The concrete failure it
 * prevents is a person reading "MCP" two rows under "Sipotek ApS detaljer" and
 * concluding their AI key is per-company. It is deliberately per-user
 * (ADR-007), and one key pasted per company would be a different product.
 *
 * The two user entries are one menu rather than two buttons. The header already
 * holds a burger, a title, the switcher and sign-out; four more pixels of
 * chrome on a phone is where a company indicator starts getting pushed off, and
 * that indicator is the one thing here that must never disappear.
 */

interface NavItem {
  readonly to: string;
  /* A key rather than a label: the nav renames itself with the language. */
  readonly label: MessageKey;
}

/**
 * Where the work happens, in the order it flows: what needs attention, then the
 * ledger, then what feeds it, then what comes out of it.
 */
const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'nav.overview' },
  { to: '/kladde', label: 'nav.bookkeeping' },
  { to: '/bilag', label: 'nav.attachments' },
  { to: '/post', label: 'nav.inbound' },
  { to: '/bank', label: 'nav.bank' },
  { to: '/koerebog', label: 'nav.trips' },
  { to: '/fakturaer', label: 'nav.invoices' },
  { to: '/kontakter', label: 'nav.contacts' },
  { to: '/moms', label: 'nav.vat' },
  { to: '/rapporter', label: 'nav.reports' },
];

/**
 * Configuration rather than daily work.
 *
 * Kontoplan and SAF-T were top-level entries and are moved here; the routes and
 * the pages are unchanged. `/indstillinger` keeps its path and loses its label
 * to the company's own name.
 *
 * Mobil-app is the one entry that is not purely company configuration —
 * installing is per device — but its capture buttons save to the active
 * company, and "the app" is something a person sets up, not works in.
 */
const SETTINGS_ITEMS: readonly NavItem[] = [
  { to: '/kontoplan', label: 'nav.chartOfAccounts' },
  { to: '/saft', label: 'nav.saft' },
  { to: '/revisionsspor', label: 'nav.audit' },
  { to: '/mobil-app', label: 'nav.mobileApp' },
];

/** Neither of these changes when the company switcher moves. */
const ACCOUNT_ITEMS: readonly NavItem[] = [
  { to: '/min-konto', label: 'nav.myAccount' },
  // `/ai-adgang`, not `/mcp`: Traefik routes that prefix to the MCP server's
  // own container in production. See routes.tsx.
  { to: '/ai-adgang', label: 'nav.mcp' },
];

const SETTINGS_PATHS = ['/indstillinger', ...SETTINGS_ITEMS.map((item) => item.to)];

export function AppLayout(): ReactNode {
  const t = useT();
  const [opened, { toggle, close }] = useDisclosure(false);
  const { pathname } = useLocation();
  const { memberships, companyId } = useAuth();

  const active = memberships.find((item) => item.companyId === companyId);
  // The company's own details page carries the company's name, so the label
  // says which set of books the page is about. Falls back to the generic word
  // before a company is chosen, rather than rendering "undefined detaljer".
  const companyPageLabel =
    active?.name === undefined || active.name === null
      ? t('nav.companyDetailsFallback')
      : t('nav.companyDetails', { name: active.name });

  const settingsOpen = SETTINGS_PATHS.some((path) => pathname.startsWith(path));

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: 240,
        breakpoint: 'sm',
        // Collapsed on small screens and opened by the burger, so the app is
        // usable on a phone -- which is where a receipt gets photographed.
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label={t('shell.menu')}
            />
            <Text fw={700} visibleFrom="xs">
              {t('app.name')}
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {/*
              In the header rather than the navbar: the navbar collapses on a
              phone, and a company indicator that disappears on the device most
              likely to be used in a hurry is the one place it must not.
            */}
            {memberships.length > 0 && <CompanySwitcher />}

            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton
                  px="sm"
                  py={6}
                  aria-label={t('shell.accountMenu')}
                  style={{
                    borderRadius: 'var(--mantine-radius-xl)',
                    border: '1px solid var(--mantine-color-gray-4)',
                    background: 'var(--mantine-color-gray-0)',
                  }}
                >
                  <Group gap={6} wrap="nowrap">
                    <Text fw={600} size="sm">
                      {t('shell.account')}
                    </Text>
                    <Text size="xs" c="dimmed" aria-hidden="true">
                      ▾
                    </Text>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>{t('shell.you')}</Menu.Label>
                {ACCOUNT_ITEMS.map((item) => (
                  <Menu.Item key={item.to} component={Link} to={item.to}>
                    {t(item.label)}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            <SignOutButton />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <ScrollArea type="scroll">
          {/*
            A real <nav>, written out rather than asked of ScrollArea: neither
            AppShell.Navbar nor ScrollArea renders one, and ScrollArea is not
            polymorphic, so `component="nav"` is silently ignored. Screen
            readers get a landmark to jump to.
          */}
          <nav aria-label={t('shell.mainNav')}>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              component={RouterNavLink}
              to={item.to}
              label={t(item.label)}
              active={item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)}
              onClick={close}
            />
          ))}

          {/*
            Expanded whenever one of its pages is open, so a person who lands on
            Kontoplan by URL or by browser history sees where it lives rather
            than a collapsed group that looks unrelated to the page they are on.
          */}
          <NavLink
            label={t('nav.settings')}
            defaultOpened={settingsOpen}
            childrenOffset={16}
          >
            <NavLink
              component={RouterNavLink}
              to="/indstillinger"
              label={companyPageLabel}
              active={pathname.startsWith('/indstillinger')}
              onClick={close}
            />
            {SETTINGS_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                component={RouterNavLink}
                to={item.to}
                label={t(item.label)}
                active={pathname.startsWith(item.to)}
                onClick={close}
              />
            ))}
            </NavLink>
          </nav>
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
